import { AuthError, Session } from '@supabase/supabase-js';
import { supabase } from '@/integrations/supabase/client';

// Constants for session management
const MAX_RETRY_ATTEMPTS = 3;
const MIN_VALIDITY_WINDOW = 5 * 60 * 1000; // 5 minutes in milliseconds
const REFRESH_BUFFER = 2 * 60 * 1000; // 2 minutes before expiry
const BASE_RETRY_DELAY = 1000; // 1 second
const MAX_RETRY_DELAY = 30000; // 30 seconds max delay

// Persistence-related constants
export const PERSISTENCE_KEY = 'auth_persistence_type';
const DEFAULT_PERSISTENCE = 'session'; // 'session' or 'local'

// Helper to get the current persistence type (remember me setting)
export const getPersistenceType = (): 'session' | 'local' => {
  try {
    return (localStorage.getItem(PERSISTENCE_KEY) || DEFAULT_PERSISTENCE) as 'session' | 'local';
  } catch (error) {
    // Handle private browsing mode or storage restrictions
    console.warn('Could not access localStorage. Defaulting to session storage.', error);
    return 'session';
  }
};

// Helper to set the persistence type (remember me setting)
export const setPersistenceType = (type: 'session' | 'local'): boolean => {
  try {
    localStorage.setItem(PERSISTENCE_KEY, type);
    return true;
  } catch (error) {
    console.warn('Could not save persistence type to localStorage.', error);
    return false;
  }
};

// Helper to detect private browsing mode
export const isPrivateBrowsingMode = (): boolean => {
  try {
    // Quick test if we can use local storage
    localStorage.setItem('test_storage', 'test');
    localStorage.removeItem('test_storage');
    return false;
  } catch (e) {
    return true;
  }
};

// Helper to calculate if a session needs refresh
export const shouldRefreshSession = (session: Session | null): boolean => {
  if (!session) return false;
  
  const expiresAt = session.expires_at ? session.expires_at * 1000 : 0;
  const now = Date.now();
  
  return expiresAt - now < MIN_VALIDITY_WINDOW;
};

// Calculate delay for retry attempts with exponential backoff and jitter
const getRetryDelay = (attempt: number): number => {
  // Exponential backoff with random jitter (0-1000ms)
  const jitter = Math.random() * 1000;
  return Math.min(
    BASE_RETRY_DELAY * Math.pow(2, attempt) + jitter,
    MAX_RETRY_DELAY
  );
};

// Handles the actual session refresh with intelligent retry
export const performSessionRefresh = async (
  currentRetry = 0
): Promise<{ data: { session: Session | null; error: AuthError | null } }> => {
  try {
    console.log(`Attempting to refresh session (attempt ${currentRetry + 1}/${MAX_RETRY_ATTEMPTS + 1})`);
    
    const { data, error } = await supabase.auth.refreshSession();
    
    if (error) {
      console.error(`Session refresh error: ${error.message}`);
      
      // Check if we should retry based on the error type and retry count
      if (currentRetry < MAX_RETRY_ATTEMPTS) {
        const isRetryableError = 
          error.status !== 401 && // Don't retry auth errors (invalid/expired tokens)
          !error.message.includes('expired') && // Don't retry if explicitly expired
          error.message !== 'User not found' &&
          error.status !== 403; // Don't retry forbidden
          
        if (isRetryableError) {
          const delay = getRetryDelay(currentRetry);
          console.log(`Retrying session refresh in ${delay}ms...`);
          await new Promise(resolve => setTimeout(resolve, delay));
          return performSessionRefresh(currentRetry + 1);
        } else {
          console.log(`Non-retryable error: ${error.message}`);
        }
      } else {
        console.log(`Max retry attempts (${MAX_RETRY_ATTEMPTS}) reached for session refresh`);
      }
      
      return { data: { session: null, error } };
    }
    
    console.log('Session refreshed successfully');

    // After successful refresh, ensure the persistence type is applied
    const persistenceType = getPersistenceType();
    if (persistenceType === 'local') {
      // This will not overwrite the session but ensures cookies are set with correct expiry
      // Using setSession instead of updateSession, which is not available in the Supabase client
      if (data.session) {
        await supabase.auth.setSession(data.session);
      }
    }
    
    return { data: { session: data.session, error: null } };
  } catch (error) {
    console.error('Unexpected error during session refresh:', error);
    return { data: { session: null, error: error as AuthError } };
  }
};

// Calculate the optimal time to refresh a session
export const calculateRefreshTime = (session: Session): number => {
  if (!session.expires_at) return MIN_VALIDITY_WINDOW;
  
  const expiresAt = session.expires_at * 1000; // Convert to milliseconds
  const now = Date.now();
  const timeUntilExpiry = expiresAt - now;
  
  // If already expired or about to expire, refresh immediately
  if (timeUntilExpiry <= REFRESH_BUFFER) {
    return 0;
  }
  
  // Schedule refresh to happen REFRESH_BUFFER ms before expiry
  return Math.max(0, timeUntilExpiry - REFRESH_BUFFER);
};

// Setup automatic session refresh
export const setupSessionRefresh = (
  session: Session | null,
  onRefresh: () => Promise<void>
): (() => void) => {
  if (!session) return () => {};
  
  const refreshTime = calculateRefreshTime(session);
  
  console.log(`Scheduling session refresh in ${Math.round(refreshTime / 1000)} seconds`);
  
  const refreshTimer = setTimeout(onRefresh, refreshTime);
  
  return () => {
    console.log('Clearing scheduled session refresh');
    clearTimeout(refreshTimer);
  };
};

// Validate if a session is still valid
export const isSessionValid = (session: Session | null): boolean => {
  if (!session || !session.expires_at) return false;
  
  const expiresAt = session.expires_at * 1000;
  const now = Date.now();
  
  return expiresAt > now;
};

// Check if session is close to expiration (within the buffer time)
export const isSessionNearExpiry = (session: Session | null): boolean => {
  if (!session || !session.expires_at) return true;
  
  const expiresAt = session.expires_at * 1000;
  const now = Date.now();
  
  return expiresAt - now < REFRESH_BUFFER * 2;
};

// Detect inactive time and return true if session should be checked/refreshed
export const detectInactivity = (inactiveThreshold: number = 30 * 60 * 1000): boolean => {
  try {
    const lastActivity = localStorage.getItem('last_activity');
    if (!lastActivity) return true;
    
    const now = Date.now();
    const lastActiveTime = parseInt(lastActivity, 10);
    const inactiveTime = now - lastActiveTime;
    
    // Update the last activity time
    localStorage.setItem('last_activity', now.toString());
    
    return inactiveTime > inactiveThreshold;
  } catch (error) {
    // Can't track inactivity in private browsing, default to checking session
    return true;
  }
};

// Update last activity timestamp (to be called on user interactions)
export const updateLastActivity = (): void => {
  try {
    localStorage.setItem('last_activity', Date.now().toString());
  } catch (error) {
    // Silently fail if in private browsing
  }
};

// Enhanced function to clear all auth-related storage
export const clearAuthStorage = (): void => {
  try {
    console.log('Clearing auth storage...');
    
    // First check which persistence type we're using
    const persistenceType = getPersistenceType();
    
    // Attempt to clear browser storage based on persistence type
    if (persistenceType === 'local') {
      // Clear auth-related items from localStorage
      const authKeys = [];
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key && (
          key.startsWith('sb-') || 
          key.includes('auth') || 
          key.includes('supabase') || 
          key.includes('linkedin') ||
          key.includes('oauth')
        )) {
          authKeys.push(key);
        }
      }
      
      // Remove each auth-related key
      authKeys.forEach(key => localStorage.removeItem(key));
      console.log(`Cleared ${authKeys.length} auth-related items from localStorage`);
      
    }
    
    // Also clear sessionStorage regardless of persistence type
    // This ensures we clear any OAuth tokens that might be stored there
    const sessionAuthKeys = [];
    for (let i = 0; i < sessionStorage.length; i++) {
      const key = sessionStorage.key(i);
      if (key && (
        key.startsWith('sb-') || 
        key.includes('auth') || 
        key.includes('supabase') || 
        key.includes('linkedin') ||
        key.includes('oauth')
      )) {
        sessionAuthKeys.push(key);
      }
    }
    
    // Remove each auth-related key from sessionStorage
    sessionAuthKeys.forEach(key => sessionStorage.removeItem(key));
    console.log(`Cleared ${sessionAuthKeys.length} auth-related items from sessionStorage`);
    
    // Also clear the persistence type setting
    try {
      localStorage.removeItem(PERSISTENCE_KEY);
    } catch (e) {
      // Ignore errors in private browsing
    }
    
    return;
  } catch (error) {
    // Silent fail in private browsing mode
    console.warn('Could not clear auth storage. May be in private browsing mode.', error);
  }
};
